import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

import UserChatBubble from "../Forms/FirmUserBtn";

const FLAG_EXPLANATION =
  "This will Red-Flag this case and send a task to the people selected managing this case. A task will be created due in one hour of work time to review this case and address the issue below for the people you select. Remember that Red-Flagging a case should be the last step before directly contacting a person about the case and should only be done when necessary. Anyone can Un-Flag a case by clicking the Flag Icon to return it to green.";

const UNFLAG_EXPLANATION =
  "This will Un-Flag this case and return the Flag Icon to green. Review tasks already sent stay open, and anyone with access to the case can Un-Flag it.";

const POSITION_NUMBERS = [1, 2, 3, 4, 5, 6];

const FlagModalBody = ({ positions, flagPage, hideModal, isUnflagging, onSavingChange }) => {
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const caseWorkers = useSelector((state) => state?.caseData?.current?.workers) ?? [];
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  // Always six slots, so the layout does not jump while the positions load.
  const slots = POSITION_NUMBERS.map(
    (position) => positions.find((slot) => slot.position === position) ?? { position, staff: null }
  );

  // A refresh can change who holds a position; drop selections that no longer exist.
  useEffect(() => {
    const available = new Set(positions.flatMap(({ staff }) => (staff ? [staff.id] : [])));
    setSelected((previous) => {
      const next = previous.filter((id) => available.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [positions]);

  const toggle = (staffId) => {
    setSelected((previous) =>
      previous.includes(staffId) ? previous.filter((id) => id !== staffId) : [...previous, staffId]
    );
  };

  const canSubmit = isUnflagging || (reason.trim().length > 0 && selected.length > 0);

  const submit = async () => {
    if (!canSubmit || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    onSavingChange(true);
    try {
      await flagPage(reason.trim(), selected);
      hideModal();
    } catch (error) {
      console.error("flag error", error);
    } finally {
      savingRef.current = false;
      setSaving(false);
      onSavingChange(false);
    }
  };

  const reviewerRow = ({ position, staff }) => {
    const checked = Boolean(staff && selected.includes(staff.id));
    return (
      <div
        key={position}
        className="checkbox-wrapper d-flex align-items-center justify-content-between"
        style={{
          gap: "5px",
          cursor: staff ? "pointer" : "default",
          height: "25px",
          padding: "0 5px",
        }}
        onClick={() => staff && !saving && toggle(staff.id)}
      >
        <span className="d-flex align-items-center">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={checked}
            disabled={!staff}
            readOnly
            style={{ pointerEvents: "none" }}
          />
          <label
            className="m-l-5 text-start mb-0"
            style={{ pointerEvents: "none", color: "#000", whiteSpace: "nowrap" }}
          >
            {caseWorkers?.[position - 1]?.name || `Position ${position}`}
          </label>
        </span>
        {staff && (
          <span className="d-flex align-items-center">
            <UserChatBubble
              userId={staff.user_id}
              first_name={staff.first_name ?? ""}
              last_name={staff.last_name ?? ""}
              profilePic={staff.profile_pic_url ?? ""}
              is_active={staff.is_active}
              width={19}
              isActive={userOnlineStatus?.[staff.user_id]?.isOnline ?? false}
              hideIcon={true}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal.Header className="tfbp-modal-header">
        <Modal.Title
          className="tfbp-modal-title"
          style={{
            textTransform: "uppercase",
          }}
        >
          {isUnflagging ? "Un-Flag Case" : "Red-Flag Case"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="text-center" style={{ padding: "5px" }}>
          <span style={{ fontWeight: "600", color: "var(--primary-25)" }}>
            {isUnflagging ? UNFLAG_EXPLANATION : FLAG_EXPLANATION}
          </span>
        </div>

        {!isUnflagging && (
          <>
            <div style={{ padding: "0 5px 5px" }}>
              <textarea
                placeholder="Input the reason this case is being Red-Flagged…"
                style={{ color: "black", height: "75px", resize: "none" }}
                className="form-control p-l-5 p-0"
                value={reason}
                disabled={saving}
                onChange={(event) => setReason(event.target.value)}
                autoFocus
              />
            </div>

            <div className="m-r-0 background-main-10 height-25">
              <h4 className="client-contact-title bg-primary-5 text-center height-25 d-flex justify-content-center align-items-center">
                Send a Review Task to Case Workers Assigned to Case
              </h4>
            </div>
            <div className="d-flex background-main-2" style={{ gap: "15px" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {slots.slice(0, 3).map(reviewerRow)}
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {slots.slice(3).map(reviewerRow)}
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <div className="d-flex justify-content-between p-1">
        <Button
          variant="secondary"
          className="height-25"
          style={{ padding: "0px 12px" }}
          disabled={saving}
          onClick={hideModal}
        >
          Cancel
        </Button>
        <Button
          variant={isUnflagging ? "danger" : canSubmit ? "success" : "primary"}
          className="height-25"
          style={{ padding: "0px 12px" }}
          disabled={saving || !canSubmit}
          onClick={submit}
        >
          {isUnflagging ? "Un-Flag Case" : "Flag Case"}
        </Button>
      </div>
    </>
  );
};

export default FlagModalBody;
